import type {
  Task,
  TaskId,
  PlanEvent,
  EventId,
} from "../../../../shared/types";
import { STATUS_LABELS } from "../../../../shared/types";
import { getCategoryDotColor } from "../../../../shared/stores/category-store";
import { EventItem } from "../../../events/components/event-item/EventItem";

export function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 gap-3">
      <svg
        viewBox="0 0 24 24"
        className="w-10 h-10 text-ink-faint"
        stroke="currentColor"
        strokeWidth={2}
        fill="none"
        aria-hidden="true"
      >
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
      <p className="text-ink font-semibold text-xs">Aucun événement à venir</p>
      <p className="text-ink-faint text-[10px] text-center max-w-xs leading-relaxed">
        Les 14 prochains jours sont libres. Ajoutez des événements ou des tâches
        avec une échéance pour les voir ici.
      </p>
    </div>
  );
}

interface AgendaTaskItemProps {
  readonly task: Task;
  readonly onCycleStatus: (id: TaskId) => void;
}

// Styles pour le badge de status - identiques à TaskItemHeader
const STATUS_STYLES: Record<Task["status"], string> = {
  todo: "bg-surface-alt text-ink-muted border border-border",
  doing: "bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  done: "bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800",
};

export function AgendaTaskItem({ task, onCycleStatus }: AgendaTaskItemProps) {
  const barColor = getCategoryDotColor(task.category);
  const isDone = task.status === "done";

  return (
    <div className="relative group flex items-center gap-3 pl-4 pr-3 py-3 border-r border-border/40 hover:bg-surface-alt/30 transition-all">
      {/* Barre latérale de couleur */}
      <div className={`absolute left-0 top-0 bottom-0 w-0.5 rounded-full ${barColor}`} />

      {/* Bouton de status - même design que TaskItemHeader */}
      <button
        type="button"
        onClick={() => onCycleStatus(task.id)}
        className={`shrink-0 min-w-max px-2 py-1 text-xs font-semibold rounded-full touch-feedback
          ${STATUS_STYLES[task.status]}
          hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-accent`}
      >
        {STATUS_LABELS[task.status]}
      </button>

      {/* Titre de la tâche - même design que TaskItemHeader */}
      <span className={`flex-1 text-sm font-medium truncate ${isDone ? "line-through text-ink-faint" : "text-ink"}`}>
        {task.title}
      </span>

      {/* Tags si présents */}
      {task.tags.length > 0 && (
        <div className="flex items-center gap-1">
          {task.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-accent/10 text-accent rounded-md whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Badge priorité urgente */}
      {task.priority === "urgent" && (
        <span className="shrink-0 text-[10px] font-bold uppercase bg-danger/15 text-danger px-1.5 py-0.5 rounded-md">
          !
        </span>
      )}
    </div>
  );
}

interface DayAgendaProps {
  readonly date: string;
  readonly dateHeader: string;
  readonly events: PlanEvent[];
  readonly tasks: Task[];
  readonly onCycleStatus: (id: TaskId) => void;
  readonly onDeleteEvent: (id: EventId) => void;
  readonly onUpdateEvent: (
    id: EventId,
    updates: Partial<
      Pick<
        PlanEvent,
        | "title"
        | "date"
        | "startTime"
        | "endTime"
        | "notes"
        | "description"
        | "tags"
        | "recurrence"
        | "reminderMinutes"
        | "category"
      >
    >,
  ) => void;
  readonly onAddRecurrenceException?: (baseId: EventId, date: string) => void;
}

export function DayAgenda({
  date,
  dateHeader,
  events,
  tasks,
  onCycleStatus,
  onDeleteEvent,
  onUpdateEvent,
  onAddRecurrenceException,
}: DayAgendaProps) {
  const hasContent = events.length > 0 || tasks.length > 0;

  return (
    <div key={date} className="space-y-3">
      {/* Header du jour */}
      <h2 className="text-xs font-semibold text-ink-faint uppercase tracking-wide sticky top-0 bg-surface/95 backdrop-blur-sm py-2 -mx-1 px-1 z-10">
        {dateHeader}
      </h2>

      {/* Contenu du jour */}
      {hasContent ? (
        <div className="space-y-2">
          {events.map((event) => (
            <EventItem
              key={event.id}
              event={event}
              onDelete={onDeleteEvent}
              onUpdate={onUpdateEvent}
              onAddRecurrenceException={onAddRecurrenceException}
            />
          ))}
          {tasks.map((task) => (
            <AgendaTaskItem
              key={task.id}
              task={task}
              onCycleStatus={onCycleStatus}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-6">
          <p className="text-xs text-ink-faint">Aucune tâche ou événement</p>
        </div>
      )}
    </div>
  );
}
